import { useMemo } from 'react';
import { useQueries } from 'react-query';
import { getGraphData, buildWidgetConfig } from '../../services/meterApi';
import { financialYearStart } from '../../utils/consumptionPeriods';
import { totalOf } from '../../utils/comparisonSeries';
import { unitOf, kpiOfMeter } from '../../utils/meterKpis';

/**
 * One month of every financial year, for the bar per year.
 *
 * A year filter takes one year, so this is one request per year rather than
 * one request. They go out together and are read together, which is what
 * `useQueries` is for — a loop of hooks would be a different number of hooks on
 * a render where the company gained a year.
 *
 * The window is the calendar month inside each year, so July of 2025-2026 and
 * July of 2026-2027 are the same month of their own year whichever month that
 * year opens in.
 */

/**
 * Reads the rows out of a widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * The window covering one month of a financial year.
 *
 * `month` is an offset into the year rather than a calendar month, so a
 * July-to-June company's first month is July and an April company's is April,
 * and neither has to be special-cased.
 *
 * A year it cannot read gives nothing rather than a window. The helper it
 * builds on falls back to January of the current calendar year, which is
 * sensible for a range control a reader can see and correct, and wrong here:
 * this fires one request per year, and that fallback would draw a confident
 * bar under a label nobody recognises.
 *
 * @param {string} financialYear - Value such as '2026-2027'.
 * @param {number} month - Months into that year, 0 through 11.
 * @returns {{start: object, end: object}|null} Moment bounds, or null when the
 *   year cannot be parsed.
 */
export const monthWindow = (financialYear, month) => {
  if (!/^\d{4}\s*-\s*\d{4}$/.test(String(financialYear || '').trim())) {
    return null;
  }

  const opens = financialYearStart(financialYear);
  if (!opens?.isValid()) return null;

  const start = opens.clone().add(month, 'months').startOf('month');

  return { start, end: start.clone().endOf('month') };
};

/**
 * Fetches one month's total for every financial year given.
 *
 * A meter carries its own KPI mapping, and it carries one per financial year,
 * so the KPI is resolved for each year rather than once for the meter. A meter
 * remapped between years would otherwise be read through this year's category
 * in a year it did not belong to it, and come back empty.
 *
 * @param {object} options - What to read.
 * @param {Array<object>} options.meters - Every meter, for their mappings.
 * @param {string} options.meterId - The meter to read.
 * @param {Array<string>} options.financialYears - Years to read, in the order
 *   the bars should appear.
 * @param {number} options.month - Months into each year, 0 through 11.
 * @param {string|number} options.locationId - Location filter.
 * @param {string|number} options.subLocationId - Sub-location filter.
 * @param {boolean} options.enabled - Whether a month has been chosen yet.
 * @returns {{bars: Array<object>, unit: string, isLoading: boolean, error: *}}
 *   One bar per year, oldest first.
 */
export const useMonthAcrossYears = ({
  meters = [],
  meterId,
  financialYears = [],
  month,
  locationId,
  subLocationId,
  enabled = true,
}) => {
  const configs = useMemo(() => {
    if (!meterId || month === '' || month === null || month === undefined) {
      return [];
    }

    return financialYears.map((financialYear) => {
      const window = monthWindow(financialYear, Number(month));
      const kpi = kpiOfMeter(meters, meterId, financialYear);
      if (!window || !kpi) return { financialYear, config: null };

      const built = {
        financialYear,
        startDateTime: window.start.toISOString(),
        endDateTime: window.end.toISOString(),
        locationId: locationId?.toString() || '1',
        title: kpi.title,
        module: kpi.module,
        category: kpi.category,
        subCategory: kpi.subCategory,
        kpi: kpi.title,
        meterId: [meterId],
        kpiType: 'EXTERNAL',
        aggregationPeriodValue: 1,
        aggregationPeriodUnit: 'months',
        xAxisField: 'displayPeriods',
      };

      if (subLocationId) built.subLocationId = subLocationId.toString();

      return { financialYear, config: buildWidgetConfig(built) };
    });
  }, [meters, meterId, financialYears, month, locationId, subLocationId]);

  const results = useQueries(
    configs.map(({ config }) => ({
      queryKey: ['graphData', config],
      queryFn: () => getGraphData(config),
      enabled: enabled && !!config,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
    }))
  );

  const bars = useMemo(
    () =>
      configs.map(({ financialYear }, index) => ({
        name: financialYear,
        value: totalOf(rowsOf(results[index]?.data)),
      })),
    [configs, results]
  );

  const unit = useMemo(() => {
    const reporting = results.find((result) => rowsOf(result?.data).length);
    return unitOf(rowsOf(reporting?.data));
  }, [results]);

  return {
    bars,
    unit,
    isLoading: results.some((result) => result?.isLoading),
    error: results.find((result) => result?.error)?.error || null,
  };
};
